import { useCallback, useRef } from 'react'
import { supabase } from '../lib/supabase.js'

// useGroupCateringSuggest — thin client wrapper around the
// suggest-catering-unit edge function (Ship 4). Returns a suggest(fields)
// function the group-create modal calls on-blur to get an AI unit
// recommendation.
//
// The body shape mirrors the other group edge-fn callers (useGroupKpi):
//   { group_id, inquiry: { event_type, event_date, guest_count, notes } }
// group_id is the tenant boundary (resolveGroupOwner). The server ALWAYS
// returns a usable suggestion (real or deterministic fallback) with HTTP
// 200, so the modal can pre-select regardless. On a transport/HTTP error
// we return null and the modal stays manual (its own catch handles it).
//
// A synchronous in-flight ref dedupes overlapping on-blur calls; the
// modal also guards with suggestRequested, but this is belt-and-suspenders.
export function useGroupCateringSuggest(groupId) {
  const inFlightRef = useRef(false)

  const suggest = useCallback(async (fields) => {
    if (!groupId) return null
    if (inFlightRef.current) return null
    inFlightRef.current = true
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
      const anonKey     = import.meta.env.VITE_SUPABASE_ANON_KEY
      const { data: sess } = await supabase.auth.getSession()
      const token = sess?.session?.access_token || anonKey

      const resp = await fetch(`${supabaseUrl}/functions/v1/suggest-catering-unit`, {
        method: 'POST',
        headers: {
          'content-type':  'application/json',
          'authorization': `Bearer ${token}`,
          'apikey':        anonKey,
        },
        body: JSON.stringify({
          group_id: groupId,
          inquiry: {
            event_type:  fields?.event_type ?? null,
            event_date:  fields?.event_date ?? null,
            guest_count: fields?.guest_count ?? null,
            notes:       fields?.notes ?? null,
          },
        }),
      })
      if (!resp.ok) {
        // Transport / auth / server error — let the modal stay manual.
        return null
      }
      const payload = await resp.json()
      if (payload?.error) return null
      return payload
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[useGroupCateringSuggest] suggest failed', e)
      return null
    } finally {
      inFlightRef.current = false
    }
  }, [groupId])

  return { suggest }
}
